import * as React from "react";
import { useMemo } from "react";
import { makeStyles, tokens, Text } from "@fluentui/react-components";
import { Chat24Regular } from "@fluentui/react-icons";
import { DiscussionCommentDto } from "../../../services/discussions.service";
import CommentItem from "./CommentItem";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "60vh",
    overflowY: "auto",
  },
  threadLine: {
    position: "relative",
    marginLeft: "16px",
    paddingLeft: tokens.spacingHorizontalL,
    borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
  emptyIcon: {
    fontSize: "48px",
    color: tokens.colorNeutralForeground4,
  },
});

interface CommentListProps {
  comments: DiscussionCommentDto[];
  currentUserEmail?: string;
  onEditComment?: (comment: DiscussionCommentDto) => void;
  onArchiveComment?: (comment: DiscussionCommentDto) => void;
}

const CommentList: React.FC<CommentListProps> = ({ comments, currentUserEmail, onEditComment, onArchiveComment }) => {
  const styles = useStyles();
  const typography = useTypographyStyles();

  // Only the last comment in the thread can be edited or archived, and only by its author
  const lastComment = comments.length > 0 ? comments[comments.length - 1] : null;

  const editableCommentId = useMemo(() => {
    if (!currentUserEmail || !lastComment) return null;
    if (lastComment.createdByEmail.toLowerCase() !== currentUserEmail.toLowerCase()) return null;
    if (lastComment.statusBefore || lastComment.statusAfter) return null;
    if (lastComment.isArchived) return null;
    return lastComment.id;
  }, [comments, currentUserEmail, lastComment]);

  const archivableCommentIds = useMemo(() => {
    if (!currentUserEmail || !lastComment) return new Set<number>();
    if (lastComment.createdByEmail.toLowerCase() !== currentUserEmail.toLowerCase()) return new Set<number>();
    if (lastComment.isArchived) return new Set<number>();
    return new Set([lastComment.id]);
  }, [comments, currentUserEmail, lastComment]);

  if (comments.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Chat24Regular className={styles.emptyIcon} />
        <Text className={typography.emptyStateTitle}>
          No comments yet
        </Text>
        <Text className={typography.bodyLarge}>Be the first to reply!</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.threadLine}>
        {comments.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            isEditable={comment.id === editableCommentId}
            isArchivable={archivableCommentIds.has(comment.id)}
            onEditClick={onEditComment}
            onArchiveClick={onArchiveComment}
          />
        ))}
      </div>
    </div>
  );
};

export default CommentList;
